/**
 * 📒 Empfänger-Adressbuch & CSV-Import-Service (Büro, Wohnanlage, Hotel, Paketshop)
 *
 * Unterstützt:
 *  - Eigenes Beispiel-CSV (Download + Import, Semikolon/Komma)
 *  - Microsoft Outlook / Microsoft 365 / Entra ID (Azure AD) CSV-Exporte
 *  - Google Contacts / Google Workspace Directory CSV-Exporte
 *  - Beliebige Excel-/Tabellen-CSVs (auch ohne Kopfzeile, falls E-Mail erkennbar)
 *
 * Speicherung:
 *  - Sofort lokal in localStorage ('kiosk_address_book_v1') für verzögerungsfreie
 *    Autovervollständigung & Offline-Betrieb
 *  - Synchronisiert mit dem Server über /api/waiting-list mit status = 'dauerhaft'
 *    (bleibt nach Paket-Benachrichtigung und beim 30-Tage-Cleanup dauerhaft aktiv)
 */
import {
  getWaitingList as serverGetWaitingList,
  saveWaitingListEntry,
  patchWaitingListEntry,
} from './serverDataService';

const STORAGE_KEY = 'kiosk_address_book_v1';
const AUTO_NUM_KEY = 'kiosk_auto_number_seq';

/**
 * Normalisiert einen Spaltennamen für den Header-Abgleich.
 */
function normHeader(h) {
  return String(h || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Zerlegt eine einzelne CSV-Zeile unter Berücksichtigung von Anführungszeichen ("...").
 */
export function splitCsvLine(line, delimiter = ';') {
  const out = [];
  let cur = '';
  let inQuotes = false;
  const s = String(line || '');
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      if (inQuotes && s[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

/**
 * Erkennt das Trennzeichen (; , oder Tab) anhand der ersten Zeile.
 */
export function detectCsvDelimiter(firstLine) {
  const s = String(firstLine || '');
  let inQuotes = false;
  const counts = { ';': 0, ',': 0, '\t': 0 };
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && counts[ch] !== undefined) counts[ch]++;
  }
  if (counts[';'] >= counts[','] && counts[';'] >= counts['\t'] && counts[';'] > 0) return ';';
  if (counts['\t'] > counts[','] && counts['\t'] > 0) return '\t';
  return ',';
}

// Bekannte Spaltennamen für Microsoft, Google und Standard-Tabellen
const HEADER_ALIASES = {
  fullName: [
    'name', 'vollstaendiger name', 'full name', 'display name', 'displayname',
    'anzeigename', 'empfaenger', 'recipient', 'mitarbeiter', 'bewohner', 'mieter', 'gast',
  ],
  firstName: [
    'first name', 'firstname', 'given name', 'givenname', 'vorname', 'voornaam',
  ],
  middleName: [
    'middle name', 'middlename', 'additional name', 'additionalname', 'zweiter vorname',
  ],
  lastName: [
    'last name', 'lastname', 'family name', 'familyname', 'surname', 'nachname', 'familienname', 'achternaam',
  ],
  email: [
    'e mail', 'email', 'mail', 'e mail address', 'email address', 'emailaddress',
    'e mail adresse', 'emailadresse', 'e mail 1 value', 'e mail 2 value',
    'primary email', 'work email', 'business email', 'home email',
    'user principal name', 'userprincipalname', 'benutzerprinzipalname',
    'primarysmtpaddress', 'kontakt', 'contact', 'e mail 2 address', 'e mail 3 address',
  ],
  departmentOrRoom: [
    'abteilung raum', 'abteilung', 'department', 'organization 1 department',
    'office', 'office location', 'buero', 'buerostandort', 'physicaldeliveryofficename',
    'raum', 'zimmer', 'room', 'wohnung', 'appartement', 'apartment', 'einheit', 'unit',
    'building id', 'floor name', 'etage', 'stockwerk', 'haus', 'gebaeude',
    'strasse', 'street', 'street address', 'address 1 street', 'adresse',
    'organization 1 name', 'company', 'firma',
  ],
  shelf: [
    'regalfach', 'regalplatz', 'fach', 'regal', 'shelf', 'postfach', 'box', 'spind', 'locker',
  ],
  note: [
    'hinweis', 'hinweise', 'hinweis fuer mitarbeiter', 'notiz', 'notizen', 'bemerkung', 'bemerkungen',
    'info', 'kommentar', 'stammkunde', 'anweisung', 'hinterlegung', 'hinterlegungswunsch',
    'note', 'notes', 'comment', 'comments', 'remarks', 'instructions', 'opmerking', 'notitie',
  ],
  phone: [
    'telefon', 'phone', 'mobile', 'mobile phone', 'handy', 'mobiltelefon',
    'phone 1 value', 'business phone', 'primary phone', 'telefon geschaeftlich', 'tel',
  ],
};

function findColumnIndices(headers, aliases) {
  const normHeaders = headers.map(normHeader);
  const indices = [];
  for (const alias of aliases) {
    for (let i = 0; i < normHeaders.length; i++) {
      if (normHeaders[i] === alias && !indices.includes(i)) {
        indices.push(i);
      }
    }
  }
  return indices;
}

function firstNonEmpty(row, indices) {
  for (const idx of indices) {
    const val = row[idx];
    if (val && String(val).trim()) return String(val).trim();
  }
  return '';
}

function extractEmail(raw) {
  const s = String(raw || '').trim();
  if (!s) return '';
  // Falls "Max Mustermann <max@firma.de>" oder mehrere Adressen mit ; / ::: (Google Contacts)
  const angleMatch = s.match(/<([^>]+@[^>]+)>/);
  if (angleMatch) return angleMatch[1].trim().toLowerCase();
  const parts = s.split(/\s*(?::::|;|,|\s)\s*/);
  for (const p of parts) {
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.trim())) return p.trim().toLowerCase();
  }
  return '';
}

/**
 * Erzeugt eine deterministische ID für einen Adressbuch-Kontakt, damit Re-Importe
 * bestehende Einträge aktualisieren statt Duplikate anzulegen.
 */
export function makeContactId(name, email) {
  const raw = `${String(name || '').trim().toLowerCase()}|${String(email || '').trim().toLowerCase()}`;
  let h1 = 0x811c9dc5;
  for (let i = 0; i < raw.length; i++) {
    h1 ^= raw.charCodeAt(i);
    h1 = Math.imul(h1, 0x01000193);
  }
  return `addr_${(h1 >>> 0).toString(36)}`;
}

/**
 * Parst beliebige CSV-Inhalte (Beispiel-CSV, Microsoft Outlook/365/Entra, Google Contacts, Excel).
 * @param {string} csvText
 * @returns {{ contacts: Array<{id:string, name:string, email:string, department:string, shelf:string, note:string, phone:string}>, formatDetected: string, skipped: number }}
 */
export function parseContactsCsv(csvText) {
  const clean = String(csvText || '').replace(/^\uFEFF/, '').trim();
  if (!clean) return { contacts: [], formatDetected: 'empty', skipped: 0 };

  const lines = clean
    .split(/\r\n|\n|\r/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0 && !l.startsWith('#'));

  if (lines.length === 0) return { contacts: [], formatDetected: 'empty', skipped: 0 };

  const delimiter = detectCsvDelimiter(lines[0]);
  const firstRow = splitCsvLine(lines[0], delimiter);
  const normFirst = firstRow.map(normHeader);

  // Format-Erkennung
  let formatDetected = 'standard';
  if (normFirst.includes('given name') || normFirst.includes('e mail 1 value') || normFirst.includes('organization 1 department')) {
    formatDetected = 'google';
  } else if (normFirst.includes('display name') || normFirst.includes('user principal name') || normFirst.includes('e mail address') || normFirst.includes('office location')) {
    formatDetected = 'microsoft';
  }

  // Prüfen, ob Zeile 1 überhaupt ein Header ist oder schon Daten enthält (z.B. E-Mail mit @)
  const firstRowHasEmail = firstRow.some((cell) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(cell || '').trim()));
  const hasKnownHeader = normFirst.some((h) =>
    HEADER_ALIASES.fullName.includes(h) ||
    HEADER_ALIASES.firstName.includes(h) ||
    HEADER_ALIASES.lastName.includes(h) ||
    HEADER_ALIASES.email.includes(h)
  );

  const isHeaderless = firstRowHasEmail && !hasKnownHeader;
  if (isHeaderless) formatDetected = 'headerless';

  const colFullName = isHeaderless ? [0] : findColumnIndices(firstRow, HEADER_ALIASES.fullName);
  const colFirstName = isHeaderless ? [] : findColumnIndices(firstRow, HEADER_ALIASES.firstName);
  const colMiddleName = isHeaderless ? [] : findColumnIndices(firstRow, HEADER_ALIASES.middleName);
  const colLastName = isHeaderless ? [] : findColumnIndices(firstRow, HEADER_ALIASES.lastName);
  const colEmail = isHeaderless ? [1] : findColumnIndices(firstRow, HEADER_ALIASES.email);
  const colDept = isHeaderless ? [2] : findColumnIndices(firstRow, HEADER_ALIASES.departmentOrRoom);
  const colShelf = isHeaderless ? [3] : findColumnIndices(firstRow, HEADER_ALIASES.shelf);
  const colNote = isHeaderless ? [4] : findColumnIndices(firstRow, HEADER_ALIASES.note);
  const colPhone = isHeaderless ? [5] : findColumnIndices(firstRow, HEADER_ALIASES.phone);

  const startIdx = isHeaderless ? 0 : 1;
  const byId = new Map();
  let skipped = 0;

  for (let i = startIdx; i < lines.length; i++) {
    const row = splitCsvLine(lines[i], delimiter);
    if (row.every((c) => !c)) continue;

    // 1. Name bestimmen
    let name = firstNonEmpty(row, colFullName);
    const first = firstNonEmpty(row, colFirstName);
    const middle = firstNonEmpty(row, colMiddleName);
    const last = firstNonEmpty(row, colLastName);
    const combinedFirstLast = [first, middle, last].filter(Boolean).join(' ').trim();

    if (!name && combinedFirstLast) {
      name = combinedFirstLast;
    } else if (name && name.includes(',') && !combinedFirstLast) {
      // "Mustermann, Max" → "Max Mustermann"
      const parts = name.split(',').map((p) => p.trim()).filter(Boolean);
      if (parts.length === 2) name = `${parts[1]} ${parts[0]}`;
    }

    // 2. E-Mail bestimmen (auch Fallback-Suche in allen Zellen, falls Spalte abweicht)
    let email = extractEmail(firstNonEmpty(row, colEmail));
    if (!email) {
      for (const cell of row) {
        const found = extractEmail(cell);
        if (found) { email = found; break; }
      }
    }

    // Falls Name leer ist, aber eine E-Mail wie "max.mustermann@firma.de" existiert → Name ableiten
    if (!name && email) {
      const localPart = email.split('@')[0] || '';
      const words = localPart
        .replace(/[._-]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter((w) => w.length >= 2)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      if (words.length >= 1) name = words.join(' ');
    }

    if (!name || name.trim().length < 2) {
      skipped++;
      continue;
    }

    // 3. Abteilung / Raum / Wohnung (alle passenden Spalten kombinieren, z.B. "Marketing" + "Raum 302")
    const deptParts = [];
    for (const idx of colDept) {
      const val = String(row[idx] || '').trim();
      if (val && !deptParts.includes(val) && val !== name && val !== email) {
        deptParts.push(val);
      }
    }
    const department = deptParts.slice(0, 2).join(' · ');
    const shelf = firstNonEmpty(row, colShelf);
    const note = firstNonEmpty(row, colNote);
    const phone = firstNonEmpty(row, colPhone);

    const id = makeContactId(name, email || department);
    byId.set(id, {
      id,
      name: name.trim(),
      email: email || '',
      department: department || '',
      abteilung: department || '',
      shelf: shelf || '',
      standardFach: shelf || '',
      note: note || '',
      hinweis: note || '',
      phone: phone || '',
    });
  }

  return {
    contacts: Array.from(byId.values()),
    formatDetected,
    skipped,
  };
}

/**
 * Baut den Inhalt der Beispiel-CSV-Datei (mit UTF-8 BOM für Excel).
 */
export function buildExampleCsvContent() {
  return [
    '\uFEFFName;E-Mail;Abteilung / Adresse;Regalfach;Hinweis für Mitarbeiter;Telefon',
    'Max Mustermann;max.mustermann@beispiel.de;Hauptstr. 12;S1;Stammkunde – immer in Fach S1 legen;+49 170 1234567',
    'Erika Musterfrau;erika.musterfrau@beispiel.de;Marketing · Raum 302;M2;Abgabe auch an Kollegen erlaubt;',
    'Jan Jansen;j.jansen@beispiel.nl;Appartement 14B;14B;Holt Pakete meist abends ab;',
  ].join('\r\n') + '\r\n';
}

/**
 * Löst den Browser-Download der Beispiel-CSV aus.
 */
export function downloadExampleCsv() {
  const content = buildExampleCsvContent();
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'ParcelPilot_Adressbuch_Beispiel.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Liest das lokale Adressbuch (synchron für schnelle UI-Vorschläge).
 */
// Kleiner Zwischenspeicher: die Paketliste fragt je Zeile und Render ab — nur neu parsen, wenn sich der Text geändert hat
let cacheRoh = null;
let cacheListe = [];
export function getLocalAddressBook() {
  try {
    const roh = localStorage.getItem(STORAGE_KEY) || '[]';
    if (roh !== cacheRoh) {
      const raw = JSON.parse(roh);
      cacheListe = Array.isArray(raw) ? raw : [];
      cacheRoh = roh;
    }
    return cacheListe.slice();
  } catch {
    return [];
  }
}

function setLocalAddressBook(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    window.dispatchEvent(new CustomEvent('kiosk:addressbook-changed', { detail: list }));
  } catch { /* */ }
}

/**
 * Konvertiert einen Server-Wartelisteneintrag (status='dauerhaft') in ein Adressbuch-Objekt.
 */
function fromServerEntry(entry) {
  let meta = {};
  if (entry.notizen) {
    try { meta = JSON.parse(entry.notizen) || {}; } catch { meta = { note: entry.notizen }; }
  }
  const id = entry.firebase_id || makeContactId(entry.name, entry.kontakt);
  const dept = String(entry.strasse || '').trim();
  const sh = String(entry.hausnummer || meta.shelf || '').trim();
  const nt = String(meta.note || meta.hinweis || '').trim();
  return {
    id,
    name: String(entry.name || '').trim(),
    email: String(entry.kontakt || '').trim(),
    department: dept,
    abteilung: dept,
    shelf: sh,
    standardFach: sh,
    note: nt,
    hinweis: nt,
    phone: String(meta.phone || '').trim(),
  };
}

/**
 * Synchronisiert dauerhafte Adressbuch-Kontakte vom Server in den lokalen Cache.
 */
export async function syncAddressBookFromServer() {
  try {
    const all = await serverGetWaitingList();
    if (!Array.isArray(all)) return getLocalAddressBook();
    const permanent = all
      .filter((e) => e.active !== false && (e.status === 'dauerhaft' || String(e.firebase_id || '').startsWith('addr_')))
      .map(fromServerEntry)
      .filter((c) => c.name);

    const byId = new Map();
    for (const c of permanent) if (c && c.id) byId.set(c.id, c);
    // Lokal angelegte, noch NICHT hochgeladene Kontakte behalten (offline/Upload fehlgeschlagen) und erneut senden —
    // sonst löschte der Abgleich sie still (Review 21.10.2026). Server-Löschungen gelten weiter für alle übrigen.
    const offen = ausstehendeIds();
    const nachzusenden = getLocalAddressBook().filter((c) => c && c.id && offen.has(c.id) && !byId.has(c.id));
    for (const c of nachzusenden) byId.set(c.id, c);
    for (const id of offen) if (byId.has(id) && !nachzusenden.some((c) => c.id === id)) offenEntfernen(id); // ist inzwischen am Server
    if (nachzusenden.length) saveAddressBookContacts(nachzusenden).catch(() => {});
    const list = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'de'));
    setLocalAddressBook(list);
    return list;
  } catch {
    return getLocalAddressBook();
  }
}

export const loadAddressBookFromServer = syncAddressBookFromServer;

/**
 * Speichert neue oder importierte Kontakte lokal UND synchronisiert sie mit dem Server.
 */
export async function saveAddressBookContacts(newContacts = []) {
  const local = getLocalAddressBook();
  const byId = new Map(local.map((c) => [c.id, c]));

  const toSync = [];
  for (const raw of newContacts) {
    if (!raw || !String(raw.name || '').trim()) continue;
    const dept = String(raw.department || raw.abteilung || '').trim();
    const sh = String(raw.shelf || raw.standardFach || '').trim();
    const nt = String(raw.note || raw.hinweis || '').trim();
    const contact = {
      id: raw.id || makeContactId(raw.name, raw.email || dept),
      name: String(raw.name || '').trim(),
      email: String(raw.email || '').trim().toLowerCase(),
      department: dept,
      abteilung: dept,
      shelf: sh,
      standardFach: sh,
      note: nt,
      hinweis: nt,
      phone: String(raw.phone || '').trim(),
    };
    byId.set(contact.id, contact);
    toSync.push(contact);
  }

  const updated = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  setLocalAddressBook(updated);

  // Im Hintergrund auf den Server pushen; bis zur Bestätigung als „ausstehend" merken (siehe syncAddressBookFromServer)
  for (const c of toSync) offenMerken(c.id);
  for (const c of toSync) {
    try {
      const metaObj = {};
      if (c.phone) metaObj.phone = c.phone;
      if (c.shelf) metaObj.shelf = c.shelf;
      if (c.note) metaObj.note = c.note;
      await saveWaitingListEntry(c.id, {
        name: c.name,
        strasse: c.department || '',
        hausnummer: c.shelf || '',
        kontakt: c.email || '',
        kontaktTyp: 'email',
        status: 'dauerhaft',
        notizen: Object.keys(metaObj).length > 0 ? JSON.stringify(metaObj) : null,
        active: true,
      });
      offenEntfernen(c.id);
    } catch { /* Offline-tolerant: bleibt ausstehend und wird beim nächsten Abgleich erneut gesendet */ }
  }

  return updated;
}

// Ausstehende (noch nicht am Server bestätigte) Kontakt-IDs je Gerät
const OFFEN_KEY = 'kiosk_address_book_pending';
function ausstehendeIds() {
  try { return new Set(JSON.parse(localStorage.getItem(OFFEN_KEY) || '[]')); } catch { return new Set(); }
}
function offenSchreiben(set) { try { localStorage.setItem(OFFEN_KEY, JSON.stringify([...set].slice(-2000))); } catch { /* */ } }
function offenMerken(id) { const s = ausstehendeIds(); s.add(id); offenSchreiben(s); }
function offenEntfernen(id) { const s = ausstehendeIds(); if (s.delete(id)) offenSchreiben(s); }

/**
 * Löscht einen Kontakt aus dem Adressbuch (lokal + Server).
 */
export async function deleteAddressBookContact(id) {
  const next = getLocalAddressBook().filter((c) => c.id !== id);
  setLocalAddressBook(next);
  offenEntfernen(id); // ein gelöschter, nie hochgeladener Kontakt darf nicht nachgesendet werden
  try {
    await patchWaitingListEntry(id, { active: false, status: 'geloescht' });
  } catch { /* */ }
  return next;
}

/**
 * Löscht alle Kontakte aus dem Adressbuch.
 */
export async function clearAddressBook() {
  const current = getLocalAddressBook();
  setLocalAddressBook([]);
  for (const c of current) {
    try { await patchWaitingListEntry(c.id, { active: false, status: 'geloescht' }); } catch { /* */ }
  }
  return [];
}

/**
 * Prüft, ob die Empfänger-/Stammkunden-Liste (CSV-Adressbuch) im Setup eingeschaltet ist.
 * Bei Paketshop / Pakketpunt / Gepäck ist sie standardmäßig AUS ('0'),
 * bei Büro / Wohnanlage / Click & Collect standardmäßig EIN ('1').
 */
export function isAddressBookEnabled() {
  try {
    const explicit = localStorage.getItem('kiosk_addressbook_enabled');
    if (explicit === '1') return true;
    if (explicit === '0') return false;
    const branche = localStorage.getItem('kiosk_branche') || 'paketshop';
    return branche === 'buero' || branche === 'wohnanlage' || branche === 'click_collect';
  } catch {
    return false;
  }
}

/**
 * Liefert den eingestellten Modus bei Treffern auf der Empfänger-/Stammkunden-Liste:
 *  - 'staff' : (a) Nur Mitarbeiter am Bildschirm informieren (Banner/Popup mit Notiz & Stammfach, keine E-Mail)
 *  - 'ask'   : (b) Mitarbeiter informieren & nach Speichern fragen, ob E-Mail gesendet werden soll
 *  - 'auto'  : (c) Mitarbeiter informieren + vollautomatisch Abhol-E-Mail senden
 */
export function getListNotifyMode() {
  try {
    const mode = localStorage.getItem('kiosk_list_notify_mode');
    if (mode === 'staff' || mode === 'ask' || mode === 'auto') return mode;
    if (localStorage.getItem('kiosk_auto_notify') === '1') return 'auto';
    const branche = localStorage.getItem('kiosk_branche') || 'paketshop';
    return (branche === 'paketshop' || branche === 'pakketpunt_nl') ? 'staff' : 'ask';
  } catch {
    return 'staff';
  }
}

function normPersonName(str) {
  return String(str || '')
    .toLowerCase()
    .trim()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Prüft, ob ein Paket-Name (beim Scannen/Erfassen oder in der Paketliste bei Ausgabe)
 * zu einer Person auf der Empfänger-/Stammkunden-Liste gehört.
 * Gibt den passenden Kontakt (inkl. note, shelf, department, email) zurück oder null.
 */
export function findBestAddressBookMatch(nameQuery) {
  if (!isAddressBookEnabled()) return null;
  const qNorm = normPersonName(nameQuery);
  if (!qNorm || qNorm.length < 3 || String(nameQuery || '').trim().startsWith('#')) return null;

  const book = getLocalAddressBook();
  if (!book.length) return null;

  const qWords = qNorm.split(' ').filter((w) => w.length >= 2);
  let best = null;
  let bestScore = 0;

  for (const c of book) {
    if (!c || !c.name) continue;
    const cNorm = normPersonName(c.name);
    if (!cNorm) continue;

    if (cNorm === qNorm) {
      return c; // Exakter Treffer
    }

    const cWords = cNorm.split(' ').filter((w) => w.length >= 2);
    if (!cWords.length || !qWords.length) continue;

    // Vor-/Nachname vertauscht ("Mustermann Max" === "Max Mustermann")
    if (
      cWords.length === qWords.length &&
      cWords.every((cw) => qWords.includes(cw))
    ) {
      return c;
    }

    // Alle Wörter des Kontakts im Paketnamen enthalten (oder umgekehrt bei >= 2 Wörtern)
    if (cWords.length >= 2 && cWords.every((cw) => qWords.some((qw) => qw === cw || (qw.length >= 4 && cw.length >= 4 && (qw.startsWith(cw) || cw.startsWith(qw)))))) {
      const score = 92;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    } else if (qWords.length >= 2 && qWords.every((qw) => cWords.some((cw) => cw === qw))) {
      const score = 88;
      if (score > bestScore) {
        bestScore = score;
        best = c;
      }
    }
  }

  return best;
}

/**
 * Sucht passende Kontakte im lokalen Adressbuch für Autovervollständigung & Live-Hinweis.
 */
export function searchAddressBook(query, limit = 5) {
  if (!isAddressBookEnabled()) return [];
  const q = String(query || '').trim().toLowerCase();
  if (!q || q.length < 2) return [];
  const book = getLocalAddressBook();
  if (!book.length) return [];

  const tokens = q.split(/\s+/).filter(Boolean);
  const scored = [];

  for (const c of book) {
    const nameLow = (c.name || '').toLowerCase();
    const deptLow = (c.department || '').toLowerCase();
    const emailLow = (c.email || '').toLowerCase();
    const shelfLow = (c.shelf || '').toLowerCase();
    const noteLow = (c.note || '').toLowerCase();

    if (nameLow === q) {
      scored.push({ contact: c, score: 100 });
      continue;
    }
    const allTokensMatch = tokens.every((t) => nameLow.includes(t) || deptLow.includes(t) || emailLow.includes(t) || noteLow.includes(t) || shelfLow === t);
    if (allTokensMatch) {
      const starts = nameLow.startsWith(q) ? 90 : 80;
      scored.push({ contact: c, score: starts });
    }
  }

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.contact);
}

// ============================================================
// 🔢 Automatische Laufnummer (#001, #002…) für Gepäck / Garderobe / Fundbüro
// ============================================================

export function isAutoNumberEnabled() {
  try { return localStorage.getItem('kiosk_auto_number') === '1'; } catch { return false; }
}

export function isIdFirstEnabled() {
  try { return localStorage.getItem('kiosk_id_first') === '1'; } catch { return false; }
}

export function isPrivacyConsentEnabled() {
  try { return localStorage.getItem('kiosk_privacy_consent') === '1'; } catch { return false; }
}

export function getPrivacyConsentText(lang = 'de') {
  try {
    const custom = localStorage.getItem('kiosk_privacy_text');
    if (custom && custom.trim()) return custom.trim();
  } catch { /* */ }
  if (lang === 'nl') {
    return 'Ik ga ermee akkoord dat mijn naam / foto / nummer uitsluitend wordt opgeslagen voor de afgifte en na het ophalen automatisch wordt verwijderd.';
  }
  if (lang === 'de') {
    return 'Ich bin damit einverstanden, dass meine Angaben (Name / Foto / Nummer) ausschließlich zur Aufbewahrung und Zuordnung gespeichert und nach der Abholung fristgerecht automatisch gelöscht werden.';
  }
  return 'I agree that my name / photo / number is stored solely for handover and will be automatically deleted after pickup.';
}

/**
 * 🔐 Deterministische 4-stellige Abhol-PIN (1000..9999) aus Paket-ID für Abhol-E-Mails & Schnellsuche.
 */
export function getPackagePickupPin(paketOrId) {
  if (!paketOrId) return '';
  if (typeof paketOrId === 'object') {
    if (paketOrId.pickupPin && /^\d{4}$/.test(String(paketOrId.pickupPin))) {
      return String(paketOrId.pickupPin);
    }
    if (!paketOrId.id) return '';
  }
  const raw = typeof paketOrId === 'object' ? String(paketOrId.id || '') : String(paketOrId);
  if (!raw) return '';
  let hash = 2166136261;
  for (let i = 0; i < raw.length; i++) {
    hash ^= raw.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const pin = 1000 + (Math.abs(hash >>> 0) % 9000);
  return String(pin);
}

/**
 * Ermittelt die nächste freie Laufnummer (#001, #002 …) anhand des Zählers und offener Pakete.
 */
export function getNextAutoNumber(pakete = []) {
  let lastSeq = 0;
  try {
    lastSeq = parseInt(localStorage.getItem(AUTO_NUM_KEY) || '0', 10) || 0;
  } catch { /* */ }

  const usedNumbers = new Set();
  for (const p of (Array.isArray(pakete) ? pakete : [])) {
    if (!p || p.ausgegeben || p.zurueckgeschickt || p.ausgegebenAm) continue;
    const m = String(p.name || '').match(/#0*(\d+)/);
    if (m) usedNumbers.add(parseInt(m[1], 10));
  }

  let candidate = (lastSeq % 999) + 1;
  let guard = 0;
  while (usedNumbers.has(candidate) && guard < 1000) {
    candidate = (candidate % 999) + 1;
    guard++;
  }
  return `#${String(candidate).padStart(3, '0')}`;
}

/**
 * Speichert die zuletzt vergebene Laufnummer im lokalen Zähler, sobald ein Paket gespeichert wird.
 */
export function commitAutoNumberIfUsed(nameStr) {
  const m = String(nameStr || '').match(/#0*(\d+)/);
  if (!m) return;
  const num = parseInt(m[1], 10);
  if (num > 0) {
    try { localStorage.setItem(AUTO_NUM_KEY, String(num)); } catch { /* */ }
  }
}
